import type { ObjectMetadata } from '@olytic/foundation';

import { OBJECT_DEFS } from '../../objects/objectDefs';
import { asDate, asDateTime, column, createdByField, field, lastModifiedByField, lookup, related, type Row, text, urlField } from './kit';

const def = OBJECT_DEFS['research-sources'];

/** A finding's quote check in a related list. */
export const yesNo = (value: unknown): string => (value === true ? 'Yes' : 'No');

/** A paper, report or article in the research library (0095). Rationale: research-sources.md. */
export const researchSources: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Research Source',
  plural: def.label,
  icon: def.icon,
  listColumns: def.columns,
  details: [
    field('title', 'Title'),
    field('publisher', 'Publisher'),
    field('published_on', 'Published', { format: asDate }),
    field('quality_grade', 'Quality Grade', {
      help: 'A: primary data, disclosed method. B: primary data, partial method. C: secondary synthesis. D: opinion, no data.',
    }),
    urlField('url', 'URL'),
    field('tags', 'Tags'),
    field('caveat', 'Caveat', { wide: true, help: 'Shown wherever the source is cited.' }),
    field('superseded_by_name', 'Superseded by', { link: lookup('research-sources', 'superseded_by_id') }),
    field('abstract', 'Abstract', { wide: true }),
    field('methodology', 'Methodology', { wide: true }),
    field('document_name', 'Document', { link: lookup('documents', 'document_id') }),
    field('created_at', 'Created', { format: asDateTime }),
    field('updated_at', 'Last Modified', { format: asDateTime }),
    createdByField(),
    lastModifiedByField(),
  ],
  sections: [
    { key: 'identity', fields: ['title', 'publisher', 'published_on', 'quality_grade', 'url', 'tags', 'caveat', 'superseded_by_name'] },
    { key: 'summary', label: 'Summary', fields: ['abstract', 'methodology'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['document_name', 'created_at', 'created_by_name', 'updated_at', 'last_modified_by_name'] },
  ],
  // Overview holds every field; the PDF opens from the header (ObjectRecordView). Findings and
  // figures read as tables, full width, on their own tab (Josh, 2026-10-29).
  tabs: [
    { key: 'overview', label: 'Overview', details: true },
    { key: 'findings-figures', label: 'Findings & Figures', related: ['research-findings', 'research-figures'] },
  ],
  related: [
    {
      ...related('research-findings', 'Findings', 'research-findings', [
        column('name', 'Statement', { primary: true }),
        column('page_number', 'Page', { align: 'right' }),
        column('quote_verified', 'Quote Verified', { format: yesNo }),
        column('figure_name', 'Figure', { link: lookup('research-figures', 'figure_id') }),
        column('tags', 'Tags'),
      ]),
      previewCount: 50,
    },
    {
      ...related('research-figures', 'Figures', 'research-figures', [
        column('name', 'Title', { primary: true }),
        column('page_number', 'Page', { align: 'right' }),
        column('description', 'Description'),
      ]),
      previewCount: 50,
    },
  ],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'publisher', 'tags'],
};
